import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAdminCatalog = () => {
  const [categories, setCategories] = useState([]);
  const [expandedCat, setExpandedCat] = useState(null);
  const [catServices, setCatServices] = useState({});
  const [loadingSvcs, setLoadingSvcs] = useState(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatIcon, setNewCatIcon] = useState('');
  const [showAddService, setShowAddService] = useState(null);
  const [newSvcName, setNewSvcName] = useState('');
  const [newSvcPrice, setNewSvcPrice] = useState('');
  const [newSvcDuration, setNewSvcDuration] = useState('');
  const [editingIcon, setEditingIcon] = useState(null);
  const [iconUrl, setIconUrl] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/categories`);
      setCategories(res.data.categories || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchCatServices = async (catId) => {
    setLoadingSvcs(catId);
    try {
      const res = await axios.get(`${API_URL}/api/admin/categories/${catId}/services`);
      setCatServices(prev => ({ ...prev, [catId]: res.data.services || [] }));
    } catch (e) { console.error(e); }
    setLoadingSvcs(null);
  };

  const toggleExpand = (catId) => {
    if (expandedCat === catId) {
      setExpandedCat(null);
    } else {
      setExpandedCat(catId);
      if (!catServices[catId]) fetchCatServices(catId);
    }
  };

  const handleToggleCategory = async (catId) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/categories/${catId}/toggle`, {});
      setCategories(prev => prev.map(c => c.category_id === catId ? { ...c, active: res.data.active } : c));
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleSaveIcon = async (catId) => {
    try {
      await axios.put(`${API_URL}/api/admin/categories/${catId}`, { icon_url: iconUrl });
      setCategories(prev => prev.map(c => c.category_id === catId ? { ...c, icon_url: iconUrl } : c));
      setEditingIcon(null); setIconUrl('');
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await axios.post(`${API_URL}/api/admin/categories`, { name: newCatName, icon_url: newCatIcon });
      setNewCatName(''); setNewCatIcon(''); setShowAddCategory(false);
      fetchCategories();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm('Delete this custom category?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/categories/${catId}`);
      fetchCategories();
    } catch (e) { alert(e.response?.data?.detail || 'Cannot delete built-in categories'); }
  };

  const handleAddService = async (catId) => {
    if (!newSvcName.trim()) return;
    try {
      await axios.post(`${API_URL}/api/admin/categories/${catId}/services`, {
        name: newSvcName, price: parseFloat(newSvcPrice) || 0, duration_minutes: parseInt(newSvcDuration) || null
      });
      setNewSvcName(''); setNewSvcPrice(''); setNewSvcDuration(''); setShowAddService(null);
      fetchCatServices(catId);
      fetchCategories();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleToggleService = async (catId, svcId) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/categories/${catId}/services/${svcId}/toggle`, {});
      setCatServices(prev => ({ ...prev, [catId]: (prev[catId] || []).map(s => s.service_id === svcId ? { ...s, active: res.data.active } : s) }));
    } catch (e) { alert('Failed'); }
  };

  const handleDeleteService = async (catId, svcId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/categories/${catId}/services/${svcId}`);
      setCatServices(prev => ({ ...prev, [catId]: (prev[catId] || []).filter(s => s.service_id !== svcId) }));
      fetchCategories();
    } catch (e) { alert('Failed'); }
  };

  const handleReorderCategories = async (ids) => {
    setCategories(prev => {
      const map = Object.fromEntries(prev.map(c => [c.category_id, c]));
      return ids.map(id => map[id]).filter(Boolean);
    });
    try {
      await axios.put(`${API_URL}/api/admin/categories/order`, { order: ids });
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save order');
      fetchCategories();
    }
  };

  const handleMoveCategory = async (catId, direction) => {
    const ids = categories.map(c => c.category_id);
    const i = ids.indexOf(catId);
    const j = direction === 'up' ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await handleReorderCategories(ids);
  };

  return {
    categories, expandedCat, catServices, loadingSvcs, editingIcon, iconUrl,
    showAddCategory, newCatName, newCatIcon, showAddService, newSvcName, newSvcPrice, newSvcDuration,
    fetchCategories, toggleExpand, handleToggleCategory, handleSaveIcon, handleAddCategory,
    handleDeleteCategory, handleAddService, handleToggleService, handleDeleteService, handleMoveCategory, handleReorderCategories,
    setEditingIcon, setIconUrl, setShowAddCategory, setNewCatName, setNewCatIcon,
    setShowAddService, setNewSvcName, setNewSvcPrice, setNewSvcDuration,
  };
};

export const useAdminAds = () => {
  const [ads, setAds] = useState([]);
  const [showAddAd, setShowAddAd] = useState(false);
  const emptyAdForm = { title: '', type: 'banner', media_url: '', link_url: '', position: 'homepage_top', width: '', height: '', listing_id: '', category_id: '', schedule_start: '', schedule_end: '' };
  const [adForm, setAdForm] = useState(emptyAdForm);
  const [adStats, setAdStats] = useState({ total: 0, active: 0, banners: 0, videos: 0 });
  const [uploadingAd, setUploadingAd] = useState(false);
  const [addingToPlacement, setAddingToPlacement] = useState(null);

  const fetchAds = useCallback(async () => {
    try {
      const [adsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/ads`),
        axios.get(`${API_URL}/api/admin/ads/stats`),
      ]);
      setAds(adsRes.data.ads || []);
      setAdStats(statsRes.data || { total: 0, active: 0, banners: 0, videos: 0 });
    } catch (e) { console.error(e); }
  }, []);

  const handleUploadAdMedia = async (file) => {
    setUploadingAd(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/ads/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAdForm(f => ({ ...f, media_url: res.data.media_url, type: res.data.media_type }));
    } catch (e) { alert(e.response?.data?.detail || 'Upload failed'); }
    setUploadingAd(false);
  };

  const handleCreateAd = async () => {
    const form = addingToPlacement ? { ...adForm, position: addingToPlacement } : adForm;
    if (!form.title || !form.media_url) return;
    try {
      await axios.post(`${API_URL}/api/admin/ads`, form);
      setAdForm(emptyAdForm);
      setShowAddAd(false); setAddingToPlacement(null); fetchAds();
    } catch (e) { alert('Failed'); }
  };

  const handleToggleAd = async (adId) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/ads/${adId}/toggle`, {});
      setAds(prev => prev.map(a => a.ad_id === adId ? { ...a, active: res.data.active } : a));
    } catch (e) { alert('Failed'); }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm('Delete this ad?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/ads/${adId}`);
      setAds(prev => prev.filter(a => a.ad_id !== adId));
    } catch (e) { alert('Failed'); }
  };

  const handleUpdateAd = async (adId, updates) => {
    try {
      await axios.put(`${API_URL}/api/admin/ads/${adId}`, updates);
      setAds(prev => prev.map(a => a.ad_id === adId ? { ...a, ...updates } : a));
    } catch (e) { alert(e.response?.data?.detail || 'Update failed'); }
  };

  return {
    ads, adStats, showAddAd, adForm, uploadingAd, addingToPlacement,
    fetchAds, setShowAddAd, setAdForm, setAddingToPlacement,
    handleCreateAd, handleToggleAd, handleDeleteAd, handleUpdateAd, handleUploadAdMedia,
  };
};

export const useAdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [subadmins, setSubadmins] = useState([]);
  const [showAddSubadmin, setShowAddSubadmin] = useState(false);
  const [subadminForm, setSubadminForm] = useState({ name: '', email: '', password: '', permissions: [] });
  const [changingPw, setChangingPw] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [editingPerms, setEditingPerms] = useState(null);
  const [editPermsValue, setEditPermsValue] = useState([]);

  const fetchSettings = useCallback(async () => {
    try {
      const [settingsRes, subadminsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/settings`),
        axios.get(`${API_URL}/api/admin/subadmins`),
      ]);
      setSettings(settingsRes.data);
      setSubadmins(subadminsRes.data.subadmins || []);
    } catch (e) { console.error(e); }
  }, []);

  const handleToggleSetting = async (key) => {
    if (!settings) return;
    const newVal = !settings[key];
    try {
      const res = await axios.put(`${API_URL}/api/admin/settings`, { [key]: newVal });
      setSettings(res.data);
    } catch (e) { alert('Failed to update setting'); }
  };

  const handleUploadLogo = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/settings/upload-logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSettings(prev => ({ ...prev, logo_url: res.data.logo_url }));
      alert('Logo updated! Refresh pages to see the new logo.');
    } catch (e) { alert(e.response?.data?.detail || 'Failed to upload logo'); }
  };

  const handleCreateSubadmin = async () => {
    if (!subadminForm.name || !subadminForm.email || !subadminForm.password) { alert('All fields required'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/admin/subadmins`, subadminForm);
      setSubadmins(prev => [...prev, res.data]);
      setSubadminForm({ name: '', email: '', password: '', permissions: [] });
      setShowAddSubadmin(false);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to create subadmin'); }
  };

  const handleToggleSubadmin = async (userId) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/subadmins/${userId}/toggle`, {});
      setSubadmins(prev => prev.map(s => s.user_id === userId ? { ...s, active: res.data.active } : s));
    } catch (e) { alert('Failed'); }
  };

  const handleChangeSubadminPw = async (userId) => {
    if (newPw.length < 6) { alert('Min 6 characters'); return; }
    try {
      await axios.put(`${API_URL}/api/admin/subadmins/${userId}/password`, { password: newPw });
      setChangingPw(null);
      setNewPw('');
      alert('Password changed');
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteSubadmin = async (userId) => {
    if (!window.confirm('Delete this subadmin?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/subadmins/${userId}`);
      setSubadmins(prev => prev.filter(s => s.user_id !== userId));
    } catch (e) { alert('Failed'); }
  };

  const handleUpdatePermissions = async (userId) => {
    try {
      const res = await axios.put(`${API_URL}/api/admin/subadmins/${userId}/permissions`, { permissions: editPermsValue });
      setSubadmins(prev => prev.map(s => s.user_id === userId ? { ...s, permissions: res.data.permissions } : s));
      setEditingPerms(null);
      setEditPermsValue([]);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to update permissions'); }
  };

  return {
    settings, subadmins, showAddSubadmin, subadminForm, changingPw, newPw, editingPerms, editPermsValue,
    fetchSettings, setShowAddSubadmin, setSubadminForm, setChangingPw, setNewPw, setEditingPerms, setEditPermsValue,
    handleToggleSetting, handleUploadLogo, handleCreateSubadmin, handleToggleSubadmin,
    handleChangeSubadminPw, handleDeleteSubadmin, handleUpdatePermissions,
  };
};

export const useAdminLiveClasses = (activeSection) => {
  const [liveClasses, setLiveClasses] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [editingClass, setEditingClass] = useState(null);
  const [editClassForm, setEditClassForm] = useState({});
  const [showAddSessionType, setShowAddSessionType] = useState(false);
  const [newSessionTypeName, setNewSessionTypeName] = useState('');
  const [liveClassCategories, setLiveClassCategories] = useState([]);
  const [sessionTypeCategoryFilter, setSessionTypeCategoryFilter] = useState('yoga_wellness');

  const fetchLiveClasses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/live-classes`);
      setLiveClasses(res.data.classes || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchSessionTypes = useCallback(async (cat) => {
    try {
      const useCat = cat || sessionTypeCategoryFilter;
      const url = useCat ? `${API_URL}/api/admin/session-types?category=${useCat}` : `${API_URL}/api/admin/session-types`;
      const res = await axios.get(url);
      setSessionTypes(res.data.session_types || []);
    } catch (e) { console.error(e); }
  }, [sessionTypeCategoryFilter]);

  const fetchLiveClassCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/live-classes/categories`);
      setLiveClassCategories(res.data.categories || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (activeSection === 'live_classes') {
      fetchLiveClasses();
      fetchSessionTypes();
      fetchLiveClassCategories();
    }
  }, [activeSection, fetchLiveClasses, fetchSessionTypes, fetchLiveClassCategories]);

  const handleToggleLiveClassCategory = async (categoryId) => {
    try {
      await axios.post(`${API_URL}/api/admin/live-classes/categories/${categoryId}/toggle`);
      fetchLiveClassCategories();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleAdminToggleBlockClass = async (classId, blocked) => {
    try {
      await axios.post(`${API_URL}/api/admin/live-classes/${classId}/block`, { blocked });
      fetchLiveClasses();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleAdminDeleteClass = async (classId) => {
    if (!window.confirm('Delete this live session permanently?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/live-classes/${classId}`);
      fetchLiveClasses();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleAdminUpdateClass = async (classId) => {
    try {
      await axios.put(`${API_URL}/api/admin/live-classes/${classId}`, editClassForm);
      setEditingClass(null);
      setEditClassForm({});
      fetchLiveClasses();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleAddSessionType = async () => {
    if (!newSessionTypeName.trim()) return;
    try {
      await axios.post(`${API_URL}/api/admin/session-types`, {
        name: newSessionTypeName.trim(),
        category: sessionTypeCategoryFilter,
      });
      setNewSessionTypeName('');
      setShowAddSessionType(false);
      fetchSessionTypes();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleToggleSessionType = async (typeId, active) => {
    try {
      await axios.put(`${API_URL}/api/admin/session-types/${typeId}`, { active });
      fetchSessionTypes();
    } catch (e) { alert('Failed'); }
  };

  const handleDeleteSessionType = async (typeId) => {
    if (!window.confirm('Delete this session type?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/session-types/${typeId}`);
      fetchSessionTypes();
    } catch (e) { alert('Failed'); }
  };

  return {
    liveClasses, sessionTypes, liveClassCategories, sessionTypeCategoryFilter,
    editingClass, editClassForm, showAddSessionType, newSessionTypeName,
    fetchSessionTypes, setSessionTypeCategoryFilter,
    setEditingClass, setEditClassForm, setShowAddSessionType, setNewSessionTypeName,
    handleToggleLiveClassCategory, handleAdminToggleBlockClass, handleAdminDeleteClass,
    handleAdminUpdateClass, handleAddSessionType, handleToggleSessionType, handleDeleteSessionType,
  };
};
